import { Alert, Button, Dialog, Label, ManageEmailsIcon } from '@viapeople/components'
import { Trash } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../../components/GenericTable'
import { trpc } from '../../../../utils/trpc'
import { useCycleEmailConfigurationsStore } from '../../cycleEmailConfigurationsStore'
import { CycleEmailModal, type CycleEmailModalState, emailTypeOptions, type ListDisplay } from './CycleEmailModal'
import type { CycleEmailConfigurationListInput } from './types'

export const CycleEmails = () => {
	const [emailToDelete, setEmailToDelete] = useState<ListDisplay>()
	const { cycleId } = useParams<{ organizationId: string; cycleId: string }>()

	const [modalState, setModalState] = useState<CycleEmailModalState | null>(null)

	const { configureEmailModal, setConfigureEmailModal } = useOutletContext<{
		configureEmailModal: boolean
		setConfigureEmailModal: (value: boolean) => void
	}>()

	useEffect(() => {
		if (configureEmailModal) {
			setModalState({ mode: 'create' })
		}
	}, [configureEmailModal])

	const { useSetEnabled, useDeleteConfiguration } = useCycleEmailConfigurationsStore()
	const { deleteConfiguration, isDeleting } = useDeleteConfiguration()
	const { setEnabled } = useSetEnabled()

	const handleDelete = () => {
		if (!emailToDelete || isDeleting) return
		deleteConfiguration({
			id: emailToDelete.id,
			cycleId: emailToDelete.cycleId,
			onSuccess: () => setEmailToDelete(undefined),
		})
	}

	const columns: Column<ListDisplay>[] = [
		{
			field: 'emailName',
			label: 'Email name',
			sortable: true,
			render: (item) => item.configuration.emailName,
			colSpan: 2,
		},
		{
			field: 'emailType',
			label: 'Type',
			sortable: true,
			render: (item) =>
				emailTypeOptions.find((o) => o.value === item.configuration.emailType)?.label ?? item.configuration.emailType,
			colSpan: 1,
		},
		{
			field: 'enabled',
			label: 'Status',
			sortable: false,
			render: (item) => (
				<div className='flex'>
					<Label className='self-start' color={item.enabled ? 'green' : 'default'}>
						{item.enabled ? 'Enabled' : 'Disabled'}
					</Label>
				</div>
			),
			colSpan: 1,
		},
		{
			field: 'actions',
			label: 'Actions',
			sortable: false,
			render: (item) => (
				<div className='flex gap-2'>
					<Button variant='secondary' onClick={() => setModalState({ mode: 'preview', item })}>
						Preview
					</Button>
					<Button variant='secondary' onClick={() => setModalState({ mode: 'edit', item })}>
						Edit
					</Button>
					<Button
						variant={item.enabled ? 'outline-destructive' : 'primary'}
						onClick={() => setEnabled({ id: item.id, cycleId: item.cycleId, enabled: !item.enabled })}>
						{item.enabled ? 'Disable' : 'Enable'}
					</Button>
					<Button variant='outline-destructive' onClick={() => setEmailToDelete(item)}>
						<Trash className='w-4 h-4' />
					</Button>
				</div>
			),
			colSpan: 2,
		},
	]

	if (!cycleId) {
		return <Alert>No CycleId provided</Alert>
	}

	return (
		<>
			<GenericTable<ListDisplay, CycleEmailConfigurationListInput>
				columns={columns}
				isInternal
				idField='id'
				queryOptionsGenerator={({ page, perPage, search }) => ({
					...trpc.admin.cycles.emails.configurations.list.queryOptions({
						page,
						perPage,
						cycleId,
						search,
					}),
				})}
				showSearch
				showPagination
				emptyState={{
					icon: ManageEmailsIcon,
					title: 'No emails added to cycle steps',
					description: 'Get started by assigning an email to a cycle step',
					actionLabel: 'Add new email',
				}}
			/>

			<Dialog
				isOpen={!!emailToDelete}
				onClose={() => {
					if (isDeleting) return
					setEmailToDelete(undefined)
				}}
				onConfirm={handleDelete}
				isConfirmLoading={isDeleting}
				title='Delete email configuration'
				message={`Are you sure you want to delete "${emailToDelete?.configuration.emailName}"?`}
				type='delete'
				confirmLabel='Delete'
			/>

			<CycleEmailModal
				onClose={() => {
					setModalState(null)
					setConfigureEmailModal(false)
				}}
				modalState={modalState}
			/>
		</>
	)
}
